import { createContext, ReactNode, useState } from 'react';

export interface ActiveSubscriptionContextType {
  hasActiveSubscription: boolean;
  // Property (not method) signature so consumers can list it in a hook
  // dependency array without tripping `@typescript-eslint/unbound-method`.
  setHasActiveSubscription: (value: boolean) => void;
}

/**
 * Context exposing whether the company currently in the URL has an active
 * subscription. `AppShell` renders the billing tabs above the route that
 * fetches the company, so it cannot read the loaded company through the
 * `CompanyContext` (that lives below the `<Outlet />`). `CompanyShell`
 * writes the flag here after each fetch and `AppShell` reads it to gate the
 * Plan / Paquete / Crédito / Alta de línea tabs.
 */
export const ActiveSubscriptionContext =
  createContext<ActiveSubscriptionContextType>({
    hasActiveSubscription: false,
    setHasActiveSubscription: () => undefined,
  });

/**
 * Provider that owns the flag via `useState`. Mount it once around the
 * routes, above `AppShell`, so both the shell (reader) and `CompanyShell`
 * (writer) share the same instance. It defaults to `false`, which keeps the
 * billing tabs disabled on the search screen and while a company loads.
 */
export const ActiveSubscriptionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [hasActiveSubscription, setHasActiveSubscription] =
    useState<boolean>(false);

  return (
    <ActiveSubscriptionContext.Provider
      value={{ hasActiveSubscription, setHasActiveSubscription }}
    >
      {children}
    </ActiveSubscriptionContext.Provider>
  );
};
